import axios from 'axios';
import { getSignupSource } from '@/lib/attribution';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE });

// Add auth token and encryption session to requests
api.interceptors.request.use((config) => {
  // Don't overwrite Authorization if already set (e.g. replica token)
  if (!config.headers.Authorization) {
    const token = localStorage.getItem('legacylens_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  const encSession = localStorage.getItem('legacylens_enc_session');
  if (encSession) {
    config.headers['X-Encryption-Session'] = encSession;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't interfere with replica endpoints — let the component handle it
      const url = error.config?.url || '';
      if (url.includes('/replica/')) {
        return Promise.reject(error);
      }
      const detail = error.response?.data?.detail;
      if (detail === 'encryption_session_expired' || detail === 'encryption_session_required') {
        // Encryption session is stale/missing — clear it and redirect to dashboard
        localStorage.removeItem('legacylens_enc_session');
        // Dispatch a custom event so React components can react
        window.dispatchEvent(new CustomEvent('encryption-session-expired'));
        // Don't clear auth token — user is still logged in
        if (!window.location.pathname.startsWith('/dashboard')) {
          window.location.href = '/dashboard';
        }
      } else {
        // Regular auth failure — clear everything
        localStorage.removeItem('legacylens_token');
        localStorage.removeItem('legacylens_user');
        localStorage.removeItem('legacylens_enc_session');
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', { ...data, source: getSignupSource() }),
  login: (data) => api.post('/auth/login', data),
  googleAuth: (credential, ref = null) => api.post('/auth/google', { credential, ref, source: getSignupSource() }),
  me: () => api.get('/auth/me'),
  confirmAge: () => api.post('/auth/confirm-age'),
  forgotPassword: (email, language) => api.post('/auth/forgot-password', { email, language }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

// Storyteller
export const storytellerAPI = {
  list: () => api.get('/storytellers'),
  create: (data) => api.post('/storytellers', data),
  getMyProfile: () => api.get('/storytellers/me'),
  getProfile: (id) => api.get(`/storytellers/${id}`),
  updateSettings: (id, data) => api.put(`/storytellers/${id}/settings`, data),
  addIntake: (id, data) => api.post(`/storytellers/${id}/intake`, data),
  getIntake: (id) => api.get(`/storytellers/${id}/intake`),
  lookup: (name) => api.get(`/storytellers/lookup/${name}`),
  uploadFile: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/storytellers/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  invite: (id, email) => api.post(`/storytellers/${id}/invite`, { email }),
  getInvitations: (id) => api.get(`/storytellers/${id}/invitations`),
  removeInvitation: (id, email) => api.delete(`/storytellers/${id}/invitations/${encodeURIComponent(email)}`),
  reprocessTimeline: (id) => api.post(`/storytellers/${id}/reprocess-timeline`, {}, { timeout: 300000 }),
  youtubeIntake: (id, url) => api.post(`/storytellers/${id}/youtube-intake`, { url }),
  delete: (id) => api.delete(`/storytellers/${id}`),
  getProgress: (id) => api.get(`/storytellers/${id}/progress`),
  replicaConversations: (id) => api.get(`/storytellers/${id}/replica-conversations`),
  exportStory: (id) => api.get(`/storytellers/${id}/export`, { responseType: 'blob', timeout: 120000 }),
  uploadPhotoPrompt: (id, file, caption) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('caption', caption || '');
    return api.post(`/storytellers/${id}/photo-prompts?caption=${encodeURIComponent(caption || '')}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getPhotoPrompts: (id) => api.get(`/storytellers/${id}/photo-prompts`),
  updatePhotoPromptStatus: (storytellerId, promptId, status) =>
    api.patch(`/storytellers/${storytellerId}/photo-prompts/${promptId}`, { status }),
};

// Gifts
export const giftAPI = {
  checkout: (data) => api.post('/gifts/checkout', data),
  status: (sessionId) => api.get(`/gifts/status/${sessionId}`),
  redeem: (code) => api.get(`/gifts/redeem/${code}`),
  claim: (code) => api.post('/gifts/claim', { code }),
};

// Invitations (descendant view)
export const invitationAPI = {
  mine: () => api.get('/invitations/mine'),
};

// Sessions
export const sessionAPI = {
  create: (storytellerId, sourceType = 'biographer', visibility = 'private', visibleTo = []) =>
    api.post('/sessions', { storyteller_id: storytellerId, source_type: sourceType, visibility, visible_to: visibleTo }),
  list: (storytellerId) => api.get(`/sessions?storyteller_id=${storytellerId}`),
  get: (id) => api.get(`/sessions/${id}`),
  delete: (id) => api.delete(`/sessions/${id}`),
  greet: (id) => api.post(`/sessions/${id}/greet`),
  sendMessage: (id, content, language = 'hu') =>
    api.post(`/sessions/${id}/message`, { content, language }),
  appendMessage: (id, role, content) =>
    api.post(`/sessions/${id}/append-message`, { role, content }),
};

// Voice
export const voiceAPI = {
  transcribe: (audioBlob, language = 'hu') => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'recording.webm');
    formData.append('language', language);
    return api.post('/voice/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  synthesize: (text, voice = 'coral') =>
    api.post('/voice/synthesize', { text, voice }, { responseType: 'blob' }),
  createRealtimeSession: (storytellerId, sessionId) =>
    api.post('/voice/realtime-session', { storyteller_id: storytellerId, session_id: sessionId }),
};

// Journal
export const journalAPI = {
  list: (storytellerId) => api.get(`/sessions/journals/${storytellerId}`),
  prompt: (storytellerId, lang) => api.get(`/sessions/journals/${storytellerId}/prompt${lang ? `?lang=${lang}` : ''}`),
  updateVisibility: (sessionId, visibility, visibleTo = []) =>
    api.patch(`/sessions/${sessionId}/visibility`, { visibility, visible_to: visibleTo }),
};

// Timeline
export const timelineAPI = {
  get: (storytellerId) => api.get(`/timeline/${storytellerId}`),
  deleteEvent: (eventId) => api.delete(`/timeline/${eventId}`),
  uploadPhoto: (eventId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/timeline/${eventId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (eventId, photoId) => api.delete(`/timeline/${eventId}/photo/${photoId}`),
};

// Replica
export const replicaAPI = {
  unlock: (storytellerId, pin) =>
    api.post('/replica/unlock', { storyteller_id: storytellerId, pin }),
  selfAccess: (storytellerId) =>
    api.post(`/replica/self-access/${storytellerId}`),
  createConversation: (storytellerId, language, replicaToken) =>
    api.post(
      '/replica/conversations',
      { storyteller_id: storytellerId, language },
      { headers: { Authorization: `Bearer ${replicaToken}` } }
    ),
  getConversation: (conversationId, replicaToken) =>
    api.get(
      `/replica/conversations/${conversationId}`,
      { headers: { Authorization: `Bearer ${replicaToken}` } }
    ),
  chat: (storytellerId, message, language, replicaToken, conversationId = null) =>
    api.post(
      '/replica/chat',
      { storyteller_id: storytellerId, message, language, conversation_id: conversationId },
      { headers: { Authorization: `Bearer ${replicaToken}` } }
    ),
  guestChat: (shareToken, message, language) =>
    api.post('/replica/guest-chat', { share_token: shareToken, message, language }),
};

// Languages
export const languageAPI = {
  list: () => api.get('/languages'),
};

export const adminAPI = {
  dashboard: () => api.get('/admin/dashboard'),
  setFreeCredit: (data) => api.put('/admin/free-credit', data),
  grantCredit: (userId, amount, note) => api.post('/admin/grant-credit', { user_id: userId, amount, note }),
  setFacilityManager: (userId, enabled, plan = 'pilot') => api.put('/admin/set-facility-manager', { user_id: userId, enabled, facility_plan: plan }),
  usage: () => api.get('/admin/usage'),
  signupSources: (days) => api.get('/admin/signup-sources', { params: { days } }),
  restoreData: (data) => api.post('/admin/restore-data', data),
};

export const facilityAPI = {
  dashboard: () => api.get('/facility/dashboard'),
};

export const usageAPI = {
  me: () => api.get('/usage/me'),
};

// Encryption
export const encryptionAPI = {
  status: (storytellerId) => api.get(`/encryption/${storytellerId}/status`),
  enable: (storytellerId, password) => api.post(`/encryption/${storytellerId}/enable`, { password }),
  unlock: (storytellerId, password) => api.post(`/encryption/${storytellerId}/unlock`, { password }),
  setFamilyKey: (storytellerId, familyKey) => api.post(`/encryption/${storytellerId}/family-key`, { family_key: familyKey }),
  unlockFamily: (storytellerId, familyKey) => api.post(`/encryption/${storytellerId}/unlock-family`, { family_key: familyKey }),
  disable: (storytellerId, password) => api.post(`/encryption/${storytellerId}/disable`, { password }),
  lock: (storytellerId) => api.post(`/encryption/${storytellerId}/lock`),
};

// People
export const peopleAPI = {
  list: (storytellerId) => api.get(`/storytellers/${storytellerId}/people`),
  extract: (storytellerId) => api.post(`/storytellers/${storytellerId}/people/extract`, {}, { timeout: 120000 }),
  update: (storytellerId, personId, data) => api.put(`/storytellers/${storytellerId}/people/${personId}`, data),
  merge: (storytellerId, personIds, primaryId) =>
    api.post(`/storytellers/${storytellerId}/people/merge`, { person_ids: personIds, primary_id: primaryId }),
  deletePerson: (storytellerId, personId) => api.delete(`/storytellers/${storytellerId}/people/${personId}`),
};

// Memory Shares
export const memoryShareAPI = {
  preview: (storytellerId, personId, eventIds) =>
    api.post(`/storytellers/${storytellerId}/people/${personId}/share/preview`, { event_ids: eventIds }),
  send: (storytellerId, personId, data) =>
    api.post(`/storytellers/${storytellerId}/people/${personId}/share/send`, data),
  list: (storytellerId, personId) =>
    api.get(`/storytellers/${storytellerId}/people/${personId}/shares`),
  revoke: (storytellerId, shareId) =>
    api.delete(`/storytellers/${storytellerId}/shares/${shareId}`),
  publicView: (token) =>
    axios.get(`${API_BASE}/shared/${token}`),
  react: (token, eventId, emoji = '❤️', name = null) =>
    axios.post(`${API_BASE}/shared/${token}/react`, { event_id: eventId, emoji, name }),
  comment: (token, text, name, eventId = null) =>
    axios.post(`${API_BASE}/shared/${token}/comment`, { event_id: eventId, text, name }),
  contribute: (token, text, name) =>
    axios.post(`${API_BASE}/shared/${token}/contribute`, { text, name }),
  engagement: (storytellerId) =>
    api.get(`/storytellers/${storytellerId}/shares/engagement`),
  shareAccess: (token) =>
    api.post(`/replica/share-access/${token}`),
  replicaChat: (accessToken, storytellerId, message, conversationId, language) =>
    axios.post(`${API_BASE}/replica/chat`, {
      storyteller_id: storytellerId, message, conversation_id: conversationId, language
    }, { headers: { Authorization: `Bearer ${accessToken}` } }),
  replicaCreateConv: (accessToken, storytellerId, language, seedEvent = null) =>
    axios.post(`${API_BASE}/replica/conversations`, {
      storyteller_id: storytellerId, language, seed_event: seedEvent
    }, { headers: { Authorization: `Bearer ${accessToken}` } }),
};

// Voice Cloning
export const voiceCloneAPI = {
  status: (storytellerId) => api.get(`/voice/clone-status/${storytellerId}`),
  trigger: (storytellerId) => api.post(`/voice/clone/${storytellerId}`),
  delete: (storytellerId) => api.delete(`/voice/clone/${storytellerId}`),
  saveSample: (storytellerId, audioBlob, duration) => {
    const formData = new FormData();
    formData.append('file', audioBlob, 'sample.webm');
    formData.append('storyteller_id', storytellerId);
    formData.append('duration', String(duration));
    return api.post('/voice/save-sample', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  synthesize: (text, storytellerId, language, replicaToken) => {
    const config = { responseType: 'blob' };
    if (replicaToken) {
      config.headers = { Authorization: `Bearer ${replicaToken}` };
    }
    return api.post('/voice/synthesize-clone', { text, storyteller_id: storytellerId, language }, config);
  },
};

// Payments
export const paymentAPI = {
  packages: () => api.get('/payments/packages'),
  checkout: (packageId, originUrl) => api.post('/payments/checkout', { package_id: packageId, origin_url: originUrl }),
  status: (sessionId) => api.get(`/payments/status/${sessionId}`),
  history: () => api.get('/payments/history'),
};

// Affiliate
export const affiliateAPI = {
  status: () => api.get('/affiliate/status'),
  activate: () => api.post('/affiliate/activate'),
  earnings: (skip = 0, limit = 50) => api.get(`/affiliate/earnings?skip=${skip}&limit=${limit}`),
  referrals: () => api.get('/affiliate/referrals'),
  payout: (payoutType, amount = null) => api.post('/affiliate/payout', { payout_type: payoutType, amount }),
};

// Corrections / corrective session
export const correctionsAPI = {
  scan: (storytellerId) => api.post(`/storytellers/${storytellerId}/corrections/scan`, {}, { timeout: 300000 }),
  queue: (storytellerId) => api.get(`/storytellers/${storytellerId}/corrections/queue`),
  resolve: (storytellerId, itemId, action, customRight = null) =>
    api.post(`/storytellers/${storytellerId}/corrections/resolve`, { item_id: itemId, action, custom_right: customRight }),
  apply: (storytellerId, wrong, right, kind = 'place') =>
    api.post(`/storytellers/${storytellerId}/corrections/apply`, { wrong, right, kind }),
  dedupPreview: (storytellerId) => api.post(`/storytellers/${storytellerId}/corrections/dedup-preview`, {}, { timeout: 180000 }),
  dedupApply: (storytellerId, groups) => api.post(`/storytellers/${storytellerId}/corrections/dedup-apply`, { groups }, { timeout: 120000 }),
};

export default api;
